import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ButtonVariant } from 'src/components/atoms/Button';
import { TextVariant } from 'src/components/atoms/Text';
import Tile, { TileProps } from 'src/components/DUP/atoms/Tile/Tile';
import { ModalFooter, ModalHeader } from 'src/components/DUP/molecules/ManualUploadTile/styles';
import { useWizardUpload } from 'src/components/DUP/molecules/WizardUpload/useWizardUpload';
import Modal, { ModalProps, ModalVariant } from 'src/components/organisms/Modal';
import { useApplication } from 'src/context/ApplicationContext';
import { StyledButton, StyledText } from 'src/pages/DUPPage/styles';
import { UnauthenticateSessionProofType } from 'src/types/api';

export type ManualUploadProps = { proofType: UnauthenticateSessionProofType } & Pick<
  TileProps,
  'title' | 'subtitle'
> & {
    textContent: (React.ReactNode | string)[];
    buttonVariant?: ButtonVariant;
  };

const MANUAL_MODAL_TRANSLATION_PREFIX = 'dup.wizard.manualModal';

export const ManualUploadTile = ({
  title,
  subtitle,
  textContent,
  buttonVariant,
  proofType
}: ManualUploadProps) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { UploadedDocumentsSection } = useWizardUpload(proofType);

  return (
    <>
      {isModalOpen && (
        <ManualUploadModal
          showModal={isModalOpen}
          setShowModal={setIsModalOpen}
          proofType={proofType}
        />
      )}
      <Tile
        title={title}
        subtitle={subtitle}
        button={{
          onClick: () => setIsModalOpen(true),
          variant: buttonVariant,
          children: UploadedDocumentsSection ? t('dup.wizard.uploadAdditonalDocuments') : undefined
        }}
        content={UploadedDocumentsSection || textContent}
      />
    </>
  );
};

const ManualUploadModal = ({
  showModal,
  setShowModal,
  proofType
}: Required<Pick<ModalProps, 'showModal' | 'setShowModal'>> &
  Omit<ManualUploadProps, 'title' | 'subtitle' | 'textContent'>) => {
  const { t } = useTranslation();
  const {
    steps: { onFinished }
  } = useApplication();
  const { WizardUploadEl, proofIsProcessing } = useWizardUpload(proofType);
  const isPaystub = proofType === UnauthenticateSessionProofType.Paystub;

  return (
    <Modal
      variant={ModalVariant.wizard}
      title={
        <ModalHeader>
          <StyledText variant={TextVariant.h4} isBold isStretched>
            {t(`${MANUAL_MODAL_TRANSLATION_PREFIX}.title.${isPaystub ? 'paystub' : 'other'}`)}
          </StyledText>
          {isPaystub && (
            <StyledText variant={TextVariant.normal} isStretched>
              <Trans>{t(`${MANUAL_MODAL_TRANSLATION_PREFIX}.subtitle.paystub`)}</Trans>
            </StyledText>
          )}
        </ModalHeader>
      }
      blurBackground
      showModal={showModal}
      setShowModal={setShowModal}
      showModalActions={false}
      isBackClosable={false}
      isEscapeClosable={false}
      showCloseButton
    >
      {WizardUploadEl}
      <ModalFooter>
        <StyledButton
          name="cancel upload"
          onClick={() => setShowModal(false)}
          variant={ButtonVariant.outline}
        >
          {t('buttonLabel.cancel')}
        </StyledButton>
        <StyledButton
          name="next"
          isDisabled={proofIsProcessing}
          onClick={() => {
            setShowModal(false);
            onFinished();
          }}
          variant={ButtonVariant.contained}
        >
          {t('buttonLabel.next')}
        </StyledButton>
      </ModalFooter>
    </Modal>
  );
};
